import { HYDRATE } from 'next-redux-wrapper';

import { IAction } from '@r1-frontend/shared/types/Redux';
import { InferValueTypes } from '@r1-frontend/shared/types/Redux';

import { CART_DEFAULT_MAX_QUANTITY } from '../constants';

import * as actions from './actions';
import { ECartActionTypes } from './actionTypes';
import { initialState } from './initialState';
import { IAddCartItemPayload, ICartItem, ICartState } from './types';

export type TActions = ReturnType<InferValueTypes<typeof actions>>;
type THydrateAction = IAction<typeof HYDRATE, { cart?: ICartState }>;

const limitQuantity = (quantity: number, maxQuantity: number) => {
    if (quantity < 0) {
        return 0;
    }

    if (quantity > maxQuantity) {
        return maxQuantity;
    }

    return quantity;
};

const createLocalCartId = () => `local-cart-${Date.now()}-${Math.floor(Math.random() * 1000000)}`;

const createItemId = (payload: IAddCartItemPayload) => {
    const variantPart = payload.variantId ? `-${payload.variantId}` : '';

    return `${payload.productId}${variantPart}-${Date.now()}-${Math.floor(Math.random() * 1000000)}`;
};

const isSameItem = (item: ICartItem, payload: IAddCartItemPayload) => item.productId === payload.productId && item.variantId === payload.variantId;

const updateItemQuantity = (item: ICartItem, quantity: number) => {
    const nextQuantity = limitQuantity(quantity, CART_DEFAULT_MAX_QUANTITY);

    return {
        ...item,
        quantity: nextQuantity,
        isSelected: nextQuantity > 0 && item.isAvailable ? item.isSelected : false,
    };
};

const addItemToState = (state: ICartState, payload: IAddCartItemPayload): ICartState => {
    const itemIndex = state.items.findIndex(item => isSameItem(item, payload));
    const quantityToAdd = payload.quantity && payload.quantity > 0 ? payload.quantity : 1;

    if (itemIndex === -1) {
        const nextItem: ICartItem = {
            id: createItemId(payload),
            productId: payload.productId,
            variantId: payload.variantId,
            title: payload.title,
            imageUrl: payload.imageUrl,
            price: payload.price,
            quantity: limitQuantity(quantityToAdd, CART_DEFAULT_MAX_QUANTITY),
            isSelected: true,
            isAvailable: true,
            createdAt: payload.createdAt || new Date().toISOString(),
        };

        return {
            ...state,
            localCartId: state.localCartId || createLocalCartId(),
            items: [...state.items, nextItem],
        };
    }

    const currentItem = state.items[itemIndex];
    const nextQuantity = currentItem.quantity === 0
        ? quantityToAdd
        : currentItem.quantity + quantityToAdd;
    const clampedQuantity = limitQuantity(nextQuantity, CART_DEFAULT_MAX_QUANTITY);
    const nextItem = {
        ...currentItem,
        quantity: clampedQuantity,
        isSelected: clampedQuantity > 0 && currentItem.isAvailable,
    };

    return {
        ...state,
        localCartId: state.localCartId || createLocalCartId(),
        items: state.items.map((item, index) => (index === itemIndex ? nextItem : item)),
    };
};

export const cartReducer = (state: ICartState = initialState, action: TActions | THydrateAction): ICartState => {
    switch (action.type) {
        case HYDRATE: {
            return action.payload.cart || state;
        }
        case ECartActionTypes.HYDRATE_CART: {
            return {
                ...action.payload,
                isHydrated: true,
            };
        }
        case ECartActionTypes.ADD_ITEM: {
            return addItemToState(state, action.payload);
        }
        case ECartActionTypes.SET_QUANTITY: {
            const items = state.items.map(item => {
                if (item.id !== action.payload.itemId) {
                    return item;
                }

                return updateItemQuantity(item, action.payload.quantity);
            });

            return {
                ...state,
                items,
            };
        }
        case ECartActionTypes.INCREMENT_QUANTITY: {
            const items = state.items.map(item => {
                if (item.id !== action.payload.itemId) {
                    return item;
                }

                const nextQuantity = limitQuantity(item.quantity + 1, CART_DEFAULT_MAX_QUANTITY);

                return {
                    ...item,
                    quantity: nextQuantity,
                    isSelected: nextQuantity > 0 && item.isAvailable,
                };
            });

            return {
                ...state,
                items,
            };
        }
        case ECartActionTypes.DECREMENT_QUANTITY: {
            const items = state.items.map(item => {
                if (item.id !== action.payload.itemId) {
                    return item;
                }

                return updateItemQuantity(item, item.quantity - 1);
            });

            return {
                ...state,
                items,
            };
        }
        case ECartActionTypes.RESTORE_ITEM: {
            const items = state.items.map(item => {
                if (item.id !== action.payload.itemId) {
                    return item;
                }

                return {
                    ...item,
                    quantity: 1,
                    isSelected: item.isAvailable,
                };
            });

            return {
                ...state,
                items,
            };
        }
        case ECartActionTypes.DELETE_ITEM: {
            return {
                ...state,
                items: state.items.filter(item => item.id !== action.payload.itemId),
            };
        }
        case ECartActionTypes.DELETE_SELECTED_ITEMS: {
            return {
                ...state,
                items: state.items.filter(item => !item.isSelected),
            };
        }
        case ECartActionTypes.SELECT_ITEM: {
            const items = state.items.map(item => {
                if (item.id !== action.payload.itemId) {
                    return item;
                }

                return {
                    ...item,
                    isSelected: action.payload.isSelected && item.quantity > 0 && item.isAvailable,
                };
            });

            return {
                ...state,
                items,
            };
        }
        case ECartActionTypes.SELECT_ALL_AVAILABLE_ITEMS: {
            const items = state.items.map(item => ({
                ...item,
                isSelected: action.payload.isSelected && item.quantity > 0 && item.isAvailable,
            }));

            return {
                ...state,
                items,
            };
        }
        case ECartActionTypes.CLEAR_CART: {
            return {
                ...initialState,
                isHydrated: state.isHydrated,
            };
        }
        case ECartActionTypes.SET_ITEM_AVAILABILITY: {
            const items = state.items.map(item => {
                if (item.id !== action.payload.itemId) {
                    return item;
                }

                return {
                    ...item,
                    isAvailable: action.payload.isAvailable,
                    isSelected: action.payload.isAvailable ? item.isSelected && item.quantity > 0 : false,
                };
            });

            return {
                ...state,
                items,
            };
        }
        default: {
            return state;
        }
    }
};
